import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import ChatWidget from "@/components/site/ChatWidget";
import LenderMarquee from "@/components/site/LenderMarquee";
import CheckMyFileTopStrip from "@/components/site/CheckMyFileTopStrip";
import ScrollToTop from "@/components/site/ScrollToTop";
import StickyCTABar from "@/components/site/StickyCTABar";
import { SettingsProvider } from "@/context/SettingsContext";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Contact from "@/pages/Contact";
import ThankYou from "@/pages/ThankYou";
import Insights from "@/pages/Insights";
import InsightDetail from "@/pages/InsightDetail";
import Disclaimer from "@/pages/Disclaimer";
import Privacy from "@/pages/Privacy";
import Complaints from "@/pages/Complaints";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminConsultations from "@/pages/admin/AdminConsultations";
import AdminSettings from "@/pages/admin/AdminSettings";

function PublicLayout({ children }) {
    return (
        <>
            <LenderMarquee />
            <Navbar />
            <CheckMyFileTopStrip />
            <main className="pb-20">{children}</main>
            <Footer />
            <ChatWidget />
            <StickyCTABar />
        </>
    );
}

/**
 * Home is the heaviest page (mortgage-quote.me widgets, calculators, etc.)
 * and the vendor third-party scripts don't gracefully re-render after a SPA
 * unmount/remount. To make navigation snappy AND avoid empty widgets when
 * returning from /about, /services, etc., we mount Home ONCE under a
 * `display: none` toggle and let the other routes render side-by-side.
 *
 * Result: zero widget refetch, zero empty-after-nav bug.
 */
function PersistentHome() {
    const location = useLocation();
    const isHome = location.pathname === "/";
    // Hide on admin routes — they have their own layout (no public shell).
    const isAdmin = location.pathname.startsWith("/admin");
    if (isAdmin) return null;
    return (
        <div
            aria-hidden={!isHome}
            style={{ display: isHome ? "block" : "none" }}
            data-testid="persistent-home"
        >
            <PublicLayout><Home /></PublicLayout>
        </div>
    );
}

function App() {
    return (
        <div className="App min-h-screen bg-brand-bg text-brand-text">
            <BrowserRouter>
                <SettingsProvider>
                    <ScrollToTop />

                    {/* Home stays mounted across navigation so its third-party
                        widgets (mortgage-quote.me forms, calculators,
                        carousel) keep their rendered state. */}
                    <PersistentHome />

                    <Routes>
                        {/* Admin */}
                        <Route path="/admin/login" element={<AdminLogin />} />
                        <Route path="/admin" element={<AdminLayout />}>
                            <Route index element={<AdminAnalytics />} />
                            <Route path="leads" element={<AdminLeads />} />
                            <Route path="consultations" element={<AdminConsultations />} />
                            <Route path="settings" element={<AdminSettings />} />
                        </Route>

                        {/* Home is rendered by PersistentHome above — only
                            need a placeholder route so React Router doesn't
                            fall through to "*". */}
                        <Route path="/" element={null} />

                        {/* Other public pages */}
                        <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
                        <Route path="/services" element={<PublicLayout><Services /></PublicLayout>} />
                        <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
                        <Route path="/thank-you" element={<PublicLayout><ThankYou /></PublicLayout>} />
                        <Route path="/insights" element={<PublicLayout><Insights /></PublicLayout>} />
                        <Route path="/insights/:slug" element={<PublicLayout><InsightDetail /></PublicLayout>} />
                        <Route path="/disclaimer" element={<PublicLayout><Disclaimer /></PublicLayout>} />
                        <Route path="/privacy" element={<PublicLayout><Privacy /></PublicLayout>} />
                        <Route path="/complaints" element={<PublicLayout><Complaints /></PublicLayout>} />
                        <Route path="*" element={null} />
                    </Routes>
                </SettingsProvider>
            </BrowserRouter>
            <Toaster
                position="bottom-right"
                toastOptions={{
                    style: {
                        background: "#1E4F8A",
                        color: "#FFFFFF",
                        border: "1px solid #163D6C",
                        borderRadius: "8px",
                    },
                }}
            />
        </div>
    );
}

export default App;
